/**
 * One place for the device / shift-start error codes the backend returns, and what the portal shows
 * and does for each. Pages use `describeDeviceError()` instead of showing raw codes.
 */

/**
 * - `sign_in_again`: the session has no device → sign out and sign in again on this device
 * - `wait_approval`: show the waiting-for-approval screen
 * - `back`: nothing to do here → back to My shifts
 * - `read_only`: the shift runs on another device → read-only shift view
 * - `retry`: the user can correct the input and try again
 */
export type DeviceErrorAction = 'sign_in_again' | 'wait_approval' | 'back' | 'read_only' | 'retry' | null;

export type DeviceErrorInfo = { title: string; message: string; action: DeviceErrorAction };

export const DEVICE_ERROR_CODES = [
  'DEVICE_UNKNOWN',
  'DEVICE_NOT_APPROVED',
  'DEVICE_BLOCKED',
  'DEVICE_MISMATCH',
  'DEVICE_BUSY',
  'WRONG_DEVICE',
  'PAIRING_CODE_INVALID',
  'PAIRING_LOCKED',
  'INVALID_SHIFT_STATE',
  'SHIFT_ALREADY_ACTIVE',
] as const;

export type DeviceErrorCode = (typeof DEVICE_ERROR_CODES)[number];

/** Readiness blockers returned by check-in and by start (the shift cannot start on this device). */
export const START_BLOCKER_CODES = [
  'DEVICE_UNKNOWN',
  'DEVICE_NOT_APPROVED',
  'DEVICE_BLOCKED',
  'DEVICE_MISMATCH',
  'DEVICE_BUSY',
] as const;

export type StartBlockerCode = (typeof START_BLOCKER_CODES)[number];

/** `{device}` is replaced with the device name (or "another device"). */
export const DEVICE_ERRORS: Record<DeviceErrorCode, DeviceErrorInfo> = {
  DEVICE_UNKNOWN: {
    title: 'Sign in again',
    message: 'This device is not recognized. Sign in again.',
    action: 'sign_in_again',
  },
  DEVICE_NOT_APPROVED: {
    title: 'Waiting for your manager to approve this device',
    message: 'Ask your manager to approve this device in the Shop portal (Cashiers → Devices), or enter a pairing code.',
    action: 'wait_approval',
  },
  DEVICE_BLOCKED: {
    title: 'Device blocked',
    message: 'This device is blocked. Ask your manager.',
    action: 'back',
  },
  DEVICE_MISMATCH: {
    title: 'Wrong device for this shift',
    message: 'This shift must start on {device}. Use that device, or ask your manager to change the shift.',
    action: 'back',
  },
  DEVICE_BUSY: {
    title: 'Device in use',
    message: 'Another shift is running on this device. Ask your manager.',
    action: 'back',
  },
  WRONG_DEVICE: {
    title: 'Shift running on another device',
    message: 'Your shift is running on {device}. Deposits, redemptions, cash drops and ending the shift only work there.',
    action: 'read_only',
  },
  PAIRING_CODE_INVALID: {
    title: 'Code not accepted',
    message: 'The pairing code is wrong or expired. Check it and try again.',
    action: 'retry',
  },
  PAIRING_LOCKED: {
    title: 'Too many attempts',
    message: 'Too many wrong codes. Try again later, or ask your manager to approve this device.',
    action: null,
  },
  INVALID_SHIFT_STATE: {
    title: 'Shift cannot start',
    message: 'This shift can no longer be started.',
    action: 'back',
  },
  SHIFT_ALREADY_ACTIVE: {
    title: 'Shift already active',
    message: 'You already have an active shift. End it before starting another one.',
    action: 'back',
  },
};

export function isDeviceErrorCode(code: unknown): code is DeviceErrorCode {
  return typeof code === 'string' && (DEVICE_ERROR_CODES as readonly string[]).includes(code);
}

export function isStartBlockerCode(code: unknown): code is StartBlockerCode {
  return typeof code === 'string' && (START_BLOCKER_CODES as readonly string[]).includes(code);
}

/**
 * Friendly title/message/action for a backend code, or `null` if it is not a device/start code.
 * `deviceName` fills `{device}`; `serverMessage` is used for PAIRING_LOCKED (it carries the wait time).
 */
export function describeDeviceError(
  code: string | null | undefined,
  opts: { deviceName?: string | null; serverMessage?: string | null } = {}
): DeviceErrorInfo | null {
  if (!isDeviceErrorCode(code)) return null;
  const base = DEVICE_ERRORS[code];
  const message =
    code === 'PAIRING_LOCKED' && opts.serverMessage
      ? opts.serverMessage
      : base.message.replace('{device}', opts.deviceName || 'another device');
  return { ...base, message };
}

/**
 * Name of the shift's device from a WRONG_DEVICE error `data`. The spec sends `shiftDevice {id, name}`;
 * the contract table only guarantees `shiftDeviceId`, so a missing name is normal.
 */
export function shiftDeviceNameFromError(data: unknown): string | null {
  const shiftDevice = (data as { shiftDevice?: { name?: unknown } } | null | undefined)?.shiftDevice;
  return typeof shiftDevice?.name === 'string' && shiftDevice.name ? shiftDevice.name : null;
}
